import { AlertType, ResourceName, Task, TaskStatus } from "@/types";
import { Stack, Typography, Box, Checkbox, Avatar, Tooltip } from "@mui/material";
import { useUpdate, Link, useNotify, useRefresh, useGetOne, useGetIdentity } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { getInitials } from "../../utils/taskUtils";
import { DueBadge } from "../shared/DueBadge";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { formatToTitleCase, depluralize } from "@/utils/formatters/stringFormatters";
import { getPandaDocStatusDisplay } from "@/utils/formatters/getPandaDocStatusDisplay";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { stringToColor } from "@/components/common/fields/AvatarField";
import { taskRecordPath } from "../../utils/taskRecordLink";

interface TaskListItemProps {
  task: Task;
  showAssignedUser?: boolean;
  showRelatedTo?: boolean;
  showCheckbox?: boolean;
  onTaskClick?: (task: Task) => void;
}

export const TaskListItem = ({
  task,
  showAssignedUser = true,
  showRelatedTo = true,
  showCheckbox = true,
  onTaskClick,
}: TaskListItemProps) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const { data: currentUser } = useGetIdentity();
  const isCompleted = task.status === TaskStatus.COMPLETED;

  const { data: assignedUser, isLoading: assignedUserLoading } = useGetOne(
    ResourceName.USERS,
    { id: task.assigned_user_id_ref },
    { enabled: showAssignedUser && !!task.assigned_user_id_ref, retry: false }
  );

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();

    const updatePayload: Partial<Task> = {
      id: task.id,
      title: task.title,
      description: task.description,
      status: isCompleted ? TaskStatus.OPEN : TaskStatus.COMPLETED,
      completed: !isCompleted,
      due_date: task.due_date,
      assigned_user_id_ref: task.assigned_user_id_ref,
      display_claim_exhibit: task.display_claim_exhibit,
      comments: Array.isArray(task.comments) ? task.comments : [],
    };

    if (task.record && typeof task.record === "object") {
      const taskRecord = task.record;
      if (taskRecord.object_type || taskRecord.object_id || taskRecord.label) {
        updatePayload.record = {
          object_type: taskRecord.object_type,
          object_id: taskRecord.object_id,
          label: taskRecord.label,
        };
      }
    }

    void update(
      ResourceName.TASKS,
      { id: task.id, data: updatePayload },
      {
        onSuccess: () => {
          notify("Task updated", { type: AlertType.SUCCESS });
          refresh();
        },
        onError: (error) => {
          notifyError(error);
        },
      }
    );
  };

  const handleClick = () => {
    onTaskClick?.(task);
  };

  // Status display logic - PandaDoc status is more informative than generic "In Progress"
  const hasPandaDoc = !!task.pandadoc;
  const isInProgress = task.status === TaskStatus.IN_PROGRESS;
  // Only show generic status chip if in progress AND no PandaDoc (PandaDoc implies progress)
  const showStatusChip = isInProgress && !hasPandaDoc;

  const assigneeDisplayName = assignedUser?.full_name || assignedUser?.email;
  const assigneeColors = assigneeDisplayName ? stringToColor(assigneeDisplayName) : null;
  const isCurrentUser = currentUser?.id && task.assigned_user_id_ref === String(currentUser.id);

  // Build tooltip for assignee showing full context
  const getAssigneeTooltip = () => {
    if (!assignedUser) return "";
    const name = assignedUser.full_name || assignedUser.email;
    return isCurrentUser ? "Assigned to you" : `Assigned to ${name}`;
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      onClick={handleClick}
      sx={{
        px: 1.5,
        py: 1,
        cursor: onTaskClick ? "pointer" : "default",
        borderRadius: 1.5,
        transition: "background-color 0.15s",
        "&:hover": {
          bgcolor: onTaskClick ? "action.hover" : "transparent",
        },
      }}
    >
      {/* Checkbox */}
      {showCheckbox && (
        <Checkbox
          checked={isCompleted}
          onClick={handleComplete}
          size="small"
          sx={{
            p: 0,
            color: isCompleted ? "success.main" : "action.disabled",
            "&.Mui-checked": { color: "success.main" },
          }}
        />
      )}

      {/* Main content */}
      <Box flex={1} minWidth={0}>
        {/* Title */}
        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={{
            textDecoration: isCompleted && showCheckbox ? "line-through" : "none",
            color: isCompleted ? "text.disabled" : "text.primary",
          }}
        >
          {task.title}
        </Typography>

        {/* Secondary info row */}
        {showRelatedTo && task.record?.object_type && task.record?.object_id && (
          <Link
            to={taskRecordPath(task) ?? ""}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ textDecoration: "none" }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ "&:hover": { color: "primary.main", textDecoration: "underline" } }}
            >
              {task.record.label
                ? `${depluralize(formatToTitleCase(task.record.object_type))}: ${task.record.label}`
                : `${depluralize(formatToTitleCase(task.record.object_type))} #${task.record.object_id}`}
            </Typography>
          </Link>
        )}
      </Box>

      {/* Right side badges - prioritized to reduce clutter */}
      <Stack direction="row" alignItems="center" spacing={1}>
        {/* Generic status chip - only when no PandaDoc provides more specific status */}
        {showStatusChip && (
          <StatusChip
            label={formatToTitleCase(task.status)}
            color={getStatusColor(task.status) ?? ColorType.Default}
            size={UIComponentSize.Small}
          />
        )}

        {/* PandaDoc status - takes precedence as it's more informative */}
        {hasPandaDoc && (
          <StatusChip
            label={getPandaDocStatusDisplay(task.pandadoc!.status).label}
            color={getPandaDocStatusDisplay(task.pandadoc!.status).color}
            size={UIComponentSize.Small}
          />
        )}

        {/* Due date badge */}
        {task.due_date && <DueBadge dueDate={task.due_date} size={UIComponentSize.Small} />}

        {/* Assignee avatar with tooltip */}
        {showAssignedUser && assignedUser && !assignedUserLoading && (
          <Tooltip title={getAssigneeTooltip()} arrow>
            {isCurrentUser ? (
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Me
              </Typography>
            ) : (
              (assigneeColors && (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: assigneeColors.bgColor,
                    color: assigneeColors.textColor,
                  }}
                >
                  {getInitials(assigneeDisplayName)}
                </Avatar>
              )) || <span />
            )}
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
